import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { post, get } from '../../util/request';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { userInfo } from '../../redux/slice';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Qs from 'qs';
import { useTranslation } from 'react-i18next';
import './index.scss';
import axios from 'axios';
import Loading from '../../components/common/loading';
import ExitBanner from '../../components/page/exitBanner/index.js';
import { Popover } from 'antd';


const genPeriodLabel = (dateStr) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const d = new Date(dateStr + 'T00:00:00Z');
  return `${months[d.getUTCMonth()]} '${String(d.getUTCFullYear()).slice(2)}`;
};

const buildChartDual = (periods, vw, vh, pad) => {
  const n = periods.length;
  if (n < 2) return { simplePts: [], compoundPts: [], simpleLine: '', compoundLine: '', compoundFillPath: '', gridTicks: [] };
  const allVals = periods.flatMap(p => [p.simple, p.compound].filter(v => v != null && v > 0));
  const rawMax = allVals.length ? Math.max(...allVals) : 100;
  const mag = Math.pow(10, Math.floor(Math.log10(rawMax)));
  const ratio = rawMax / mag;
  const step = ratio <= 2 ? mag * 0.5 : ratio <= 5 ? mag : mag * 2;
  const niceStep = Math.ceil(rawMax / (step * 4)) * step;
  const niceMax = niceStep * 5;
  const cw = vw - pad.l - pad.r;
  const ch = vh - pad.t - pad.b;
  const sx = i => pad.l + (i / (n - 1)) * cw;
  const sy = v => pad.t + ch - (v / niceMax) * ch;
  const bottomY = pad.t + ch;
  const simplePts = periods.map((p, i) => ({ x: sx(i), y: sy(p.simple > 0 ? p.simple : 0), label: p.label, value: p.simple, date: p.date }));
  const compoundPts = periods.map((p, i) =>
    (p.compound != null && p.compound > 0) ? { x: sx(i), y: sy(p.compound), label: p.label, value: p.compound, date: p.date } : null
  );
  const originPt = { x: simplePts[0].x, y: bottomY };
  const simpleNonZero = simplePts.filter(p => p.value != null && p.value > 0);
  const simpleLine = simpleNonZero.length > 0
    ? [originPt, ...simpleNonZero].map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
    : '';
  const compoundNonNull = compoundPts.filter(Boolean);
  const compoundLine = compoundNonNull.length > 0
    ? [originPt, ...compoundNonNull].map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
    : '';
  const lastCpt = compoundNonNull[compoundNonNull.length - 1];
  const compoundFillPath = compoundNonNull.length > 1
    ? `${compoundLine} L${lastCpt.x.toFixed(1)},${bottomY} L${compoundNonNull[0].x.toFixed(1)},${bottomY} Z`
    : '';
  const gridTicks = [1, 2, 3, 4].map(i => ({ value: niceStep * i, y: sy(niceStep * i) }));
  return { simplePts, compoundPts, simpleLine, compoundLine, compoundFillPath, gridTicks };
};

const fmtUSD = v => `USD ${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtK = v => v >= 1000 ? `${parseFloat((v / 1000).toFixed(1))}K` : String(Math.round(v));

const OLIVE = '#7a7252';
const SIMPLE_COLOR = '#c4ba9a';
const CHART_ORIGIN = { label: "Jun '24", date: '2024-06-01', simple: 0, compound: 0, simpleFreq: null, compoundFreq: null };

const WLFBankIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
    <path d="M5.99844 10.2812H2.39844V18.2812H5.99844V10.2812Z" fill="#7F6E4F" />
    <path d="M13.7992 10.2812H10.1992V18.2812H13.7992V10.2812Z" fill="#7F6E4F" />
    <path d="M24 20.5703H0V23.9989H24V20.5703Z" fill="#7F6E4F" />
    <path d="M21.6 10.2812H18V18.2812H21.6V10.2812Z" fill="#7F6E4F" />
    <path d="M12 0L0 5.71429V8H24V5.71429L12 0Z" fill="#7F6E4F" />
  </svg>
);

const WLFTrendChart = ({ showModal, setShowModal, totalAccruedUSD, phaseData }) => {
  const phaseOptions = Object.keys(phaseData || {});
  const [selectedPhase, setSelectedPhase] = useState(() => phaseOptions[0] || '');
  const [mTooltip, setMTooltip] = useState(null);
  const [visibleSeries, setVisibleSeries] = useState({ simple: true, compound: true });
  const [activePeriod, setActivePeriod] = useState('All');
  const [holdingDropdownOpen, setHoldingDropdownOpen] = useState(false);
  const [periodDropdownOpen, setPeriodDropdownOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const dropdownRef = useRef(null);
  const periodDropdownRef = useRef(null);
  const toggleSeries = key => setVisibleSeries(prev => ({ ...prev, [key]: !prev[key] }));

  const MVW = 840, MVH = 256;
  const MPAD = { l: 54, r: 20, t: 20, b: 36 };

  const now = new Date();
  const accruedToDate = `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}`;

  // Per-phase compound amounts for the holding selector
  const phaseAmounts = {};
  const getPeriods = (phase) =>
    ((phaseData && phaseData[phase] && phaseData[phase].periods) || [])
      .map(p => ({ ...p, label: p.label || genPeriodLabel(p.date) }));

  phaseOptions.forEach(phase => {
    const periods = getPeriods(phase);
    const cdIdx = periods.findIndex(p => p.compoundFreq === 'Current Day' || p.simpleFreq === 'Current Day');
    phaseAmounts[phase] = cdIdx >= 0 ? (periods[cdIdx].compound ?? 0) : 0;
  });

  // Total gain over simple across all holdings for the header badge
  const totalGainOverSimple = phaseOptions.reduce((sum, phase) => {
    const periods = getPeriods(phase);
    const cdIdx = periods.findIndex(p => p.compoundFreq === 'Current Day' || p.simpleFreq === 'Current Day');
    if (cdIdx < 0) return sum;
    const compound = periods[cdIdx].compound ?? 0;
    const simpleSum = periods.slice(0, cdIdx + 1).reduce((s, p) => s + p.simple, 0);
    return sum + Math.max(0, compound - simpleSum);
  }, 0);

  const allPeriods = getPeriods(selectedPhase);
  const currentDayIdx = allPeriods.findIndex(p => p.compoundFreq === 'Current Day' || p.simpleFreq === 'Current Day');
  const pastPeriods = currentDayIdx >= 0 ? allPeriods.slice(0, currentDayIdx + 1) : allPeriods;

  const cappedDate = pastPeriods.length > 0 ? pastPeriods[pastPeriods.length - 1].date : '';

  const periodsWithOrigin = [CHART_ORIGIN, ...pastPeriods];
  const filterPeriods = periods => {
    if (activePeriod === 'All') return periods;
    if (activePeriod === 'Custom') {
      const cappedTo = toDate || cappedDate;
      return periods.filter(p => {
        const d = new Date(p.date);
        if (fromDate && d < new Date(fromDate)) return false;
        if (cappedTo && d > new Date(cappedTo)) return false;
        return true;
      });
    }
    const years = activePeriod === '1Y' ? 1 : 3;
    const cutoff = new Date(now.getFullYear() - years, now.getMonth(), now.getDate());
    return periods.filter(p => new Date(p.date) >= cutoff);
  };
  const filteredPeriods = filterPeriods(periodsWithOrigin);
  const chartPeriods = filteredPeriods.length === 1
    ? [{ ...filteredPeriods[0], simple: 0, compound: null, simpleFreq: null, compoundFreq: null, label: '' }, ...filteredPeriods]
    : filteredPeriods;

  const fmtDateLabel = d => {
    if (!d) return '';
    const dt = new Date(d);
    return `${dt.getFullYear()}/${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}`;
  };
  const effectiveToDate = toDate || cappedDate;
  const periodLabel = activePeriod === 'Custom'
    ? `${fromDate ? fmtDateLabel(fromDate) : 'Start'} – ${fmtDateLabel(effectiveToDate)}`
    : activePeriod === 'All'
      ? `Start – ${fmtDateLabel(chartPeriods[chartPeriods.length - 1]?.date || '')}`
      : `${fmtDateLabel(chartPeriods[0]?.date || '')} – ${fmtDateLabel(chartPeriods[chartPeriods.length - 1]?.date || '')}`;

  const { simplePts, compoundPts, simpleLine, compoundLine, compoundFillPath, gridTicks } =
    buildChartDual(chartPeriods, MVW, MVH, MPAD);

  useEffect(() => {
    document.body.style.overflow = showModal ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showModal]);

  useEffect(() => {
    if (!holdingDropdownOpen) return;
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setHoldingDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [holdingDropdownOpen]);

  useEffect(() => {
    if (!periodDropdownOpen) return;
    const handleClickOutside = e => {
      if (periodDropdownRef.current && !periodDropdownRef.current.contains(e.target)) {
        setPeriodDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [periodDropdownOpen]);

  const displayAmount = totalAccruedUSD != null
    ? `USD ${Number(totalAccruedUSD).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : fmtUSD(phaseAmounts[selectedPhase] ?? 0);

  const selectedMeta = (phaseData && phaseData[selectedPhase]) || { name: selectedPhase, purchasedDate: '' };

  return (
    <>
      {showModal && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            width: '880px', maxWidth: '96vw',
            padding: '0',
            boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* Title bar */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px 14px 20px',
              borderBottom: '1px solid rgba(160,140,100,0.2)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <WLFBankIcon size={18} />
                <span style={{
                  fontSize: '13px', fontWeight: 700, letterSpacing: '0.05em',
                  color: '#5a4a28', textTransform: 'uppercase',
                }}>
                  Walton Land Finance — Accrued Interest
                </span>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'rgba(200,190,170,0.5)', border: 'none',
                  borderRadius: '50%', width: '28px', height: '28px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '13px', color: '#5a4f30', fontWeight: 700, lineHeight: 1,
                }}
              >✕</button>
            </div>

            {/* Content */}
            <div style={{ padding: '20px 24px 24px' }}>
              {/* Total accrued header */}
              <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', color: '#8a7550', textTransform: 'uppercase', marginBottom: '6px' }}>
                Total Accrued Interest · All Holdings · Accrued to {accruedToDate}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <span style={{ fontSize: '34px', fontWeight: 800, color: '#1e1a0e', fontFamily: "'Avenir LT Std Heavy', sans-serif", lineHeight: 1 }}>
                  {displayAmount}
                </span>
                {totalGainOverSimple > 0 && (
                  <span style={{
                    background: 'rgba(255,255,255,0.9)', color: '#1e7e3e',
                    border: '1px solid rgba(30,126,62,0.25)',
                    borderRadius: '20px', padding: '4px 12px',
                    fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap',
                  }}>
                    ▲ +{fmtUSD(totalGainOverSimple)}
                  </span>
                )}
              </div>

              {/* Holding selector */}
              <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', color: '#8a7550', textTransform: 'uppercase', marginBottom: '8px' }}>
                By Holding · Select to Chart
              </p>
              <div ref={dropdownRef} style={{ position: 'relative', marginBottom: '16px', width: '300px' }}>
                {/* Trigger */}
                <button
                  onClick={() => setHoldingDropdownOpen(prev => !prev)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
                    background: '#fff',
                    border: '2px solid #fff',
                    outline: '1px solid rgba(140,120,80,0.3)',
                    borderRadius: holdingDropdownOpen ? '8px 8px 0 0' : '8px',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                >
                  <WLFBankIcon size={17} />
                  <span style={{ fontSize: '15px', fontWeight: 700, color: '#2a1e08' }}>
                    {selectedMeta.name}
                  </span>
                  <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                    <path
                      d={holdingDropdownOpen ? 'M1 5l4-4 4 4' : 'M1 1l4 4 4-4'}
                      stroke="#888" strokeWidth="1.5" fill="none" strokeLinecap="round"
                    />
                  </svg>
                </button>

                {/* Dropdown panel */}
                {holdingDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                    background: '#fff',
                    border: '1px solid rgba(140,120,80,0.3)',
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      padding: '9px 14px 7px',
                      fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em',
                      color: '#8a7550', textTransform: 'uppercase',
                      borderBottom: '1px solid #f0ead8',
                    }}>
                      Select Holding
                    </div>
                    {phaseOptions.map((phase, idx) => {
                      const meta = (phaseData && phaseData[phase]) || { name: phase, purchasedDate: '' };
                      const isSelected = selectedPhase === phase;
                      return (
                        <div key={phase}>
                          <button
                            onClick={() => { setSelectedPhase(phase); setMTooltip(null); setHoldingDropdownOpen(false); }}
                            style={{
                              width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
                              background: isSelected ? '#F7F2E8' : '#fff',
                              border: 'none', padding: '12px 14px',
                              cursor: 'pointer', textAlign: 'left',
                            }}
                          >
                            {/* Radio */}
                            <div style={{
                              width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                              border: `2px solid ${isSelected ? OLIVE : '#ccc'}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              {isSelected && (
                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: OLIVE }} />
                              )}
                            </div>
                            {/* Name + purchased date */}
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: '#2a1e08' }}>{meta.name}</div>
                              <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>Purchased {meta.purchasedDate}</div>
                            </div>
                          </button>
                          {idx < phaseOptions.length - 1 && (
                            <div style={{ height: '1px', background: '#f0ead8', margin: '0 14px' }} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* White chart card */}
              <div style={{
                background: '#fff', borderRadius: '10px',
                padding: '14px 16px 12px',
              }}>
                {/* Chart header row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <WLFBankIcon size={15} />
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#3a2e18' }}>
                      {selectedMeta.name}
                    </span>
                    <span style={{ fontSize: '12px', color: '#888' }}>
                      · Purchased {selectedMeta.purchasedDate}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {['1Y', '3Y', 'All'].map(p => (
                      <button key={p} onClick={() => { setActivePeriod(p); setMTooltip(null); }}
                        style={{
                          width: '36px', height: '26px',
                          borderRadius: '20px',
                          border: `1px solid ${activePeriod === p ? OLIVE : '#ddd'}`,
                          background: activePeriod === p ? OLIVE : '#fff',
                          color: activePeriod === p ? '#fff' : '#666',
                          fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                        }}
                      >{p}</button>
                    ))}
                    <div ref={periodDropdownRef} style={{ position: 'relative' }}>
                      <button
                        onClick={() => setPeriodDropdownOpen(prev => !prev)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px',
                          border: '1px solid #ddd', borderRadius: '6px',
                          padding: '3px 10px', fontSize: '12px', color: '#555',
                          background: '#fff', cursor: 'pointer',
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
                          <rect x="2" y="4" width="16" height="14" rx="2" stroke="#888" strokeWidth="1.5" fill="none" />
                          <line x1="2" y1="8" x2="18" y2="8" stroke="#888" strokeWidth="3" />
                          <line x1="7" y1="2" x2="7" y2="6" stroke="#888" strokeWidth="1.5" strokeLinecap="round" />
                          <line x1="13" y1="2" x2="13" y2="6" stroke="#888" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                        Period: <strong style={{ marginLeft: '2px' }}>{periodLabel}</strong>
                        <svg width="9" height="6" viewBox="0 0 10 6" fill="none" style={{ marginLeft: '2px' }}>
                          <path d={periodDropdownOpen ? 'M1 5l4-4 4 4' : 'M1 1l4 4 4-4'} stroke="#888" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                        </svg>
                      </button>

                      {periodDropdownOpen && (
                        <div style={{
                          position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 30,
                          background: '#fff', borderRadius: '8px',
                          border: '1px solid rgba(140,120,80,0.2)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.13)',
                          padding: '14px 14px 12px',
                          minWidth: '210px',
                        }}>
                          <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', color: '#8a7550', textTransform: 'uppercase', marginBottom: '10px' }}>
                            Custom Date Range
                          </div>

                          <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', color: '#8a7550', textTransform: 'uppercase', marginBottom: '4px' }}>
                              From
                            </div>
                            <input
                              type="date"
                              value={fromDate}
                              onChange={e => setFromDate(e.target.value)}
                              max={toDate || cappedDate}
                              style={{
                                width: '100%', padding: '7px 10px',
                                border: '1px solid #ddd', borderRadius: '6px',
                                fontSize: '13px', color: fromDate ? '#333' : '#aaa',
                                boxSizing: 'border-box', outline: 'none',
                              }}
                            />
                          </div>

                          <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', color: '#8a7550', textTransform: 'uppercase', marginBottom: '4px' }}>
                              To
                            </div>
                            <input
                              type="date"
                              value={toDate || cappedDate}
                              onChange={e => setToDate(e.target.value)}
                              max={cappedDate}
                              min={fromDate || undefined}
                              style={{
                                width: '100%', padding: '7px 10px',
                                border: '1px solid #ddd', borderRadius: '6px',
                                fontSize: '13px', color: '#333',
                                boxSizing: 'border-box', outline: 'none',
                              }}
                            />
                          </div>

                          {cappedDate && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '5px', fontSize: '11px', color: '#aaa', marginBottom: '12px', lineHeight: '1.4' }}>
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none" style={{ marginTop: '1px', flexShrink: 0 }}>
                                <rect x="2" y="4" width="16" height="14" rx="2" stroke="#ccc" strokeWidth="1.5" fill="none" />
                                <line x1="2" y1="8" x2="18" y2="8" stroke="#ccc" strokeWidth="3" />
                                <line x1="7" y1="2" x2="7" y2="6" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" />
                                <line x1="13" y1="2" x2="13" y2="6" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" />
                              </svg>
                              <span>Future dates are unavailable — capped at <strong style={{ color: '#888' }}>{fmtDateLabel(cappedDate)}</strong>.</span>
                            </div>
                          )}

                          <button
                            onClick={() => {
                              setActivePeriod('Custom');
                              setPeriodDropdownOpen(false);
                              setMTooltip(null);
                            }}
                            style={{
                              width: '100%', padding: '10px',
                              background: '#C4A862', color: '#fff',
                              border: 'none', borderRadius: '6px',
                              fontSize: '13px', fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >Apply range</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* SVG chart + HTML tooltip wrapper */}
                <div style={{ position: 'relative' }}>
                  <svg width="100%" viewBox={`0 0 ${MVW} ${MVH}`} style={{ display: 'block', overflow: 'visible' }}>
                    <defs>
                      <linearGradient id="wlfCompoundGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={OLIVE} stopOpacity="0.22" />
                        <stop offset="100%" stopColor={OLIVE} stopOpacity="0" />
                      </linearGradient>
                    </defs>

                    {gridTicks.map(({ value, y }) => (
                      <g key={value}>
                        <line x1={MPAD.l} y1={y} x2={MVW - MPAD.r} y2={y}
                          stroke="#e8e2d4" strokeWidth="1" strokeDasharray="4,4" />
                        <text x={MPAD.l - 6} y={y + 4} textAnchor="end" fontSize="11" fill="#bbb">
                          {fmtK(value)}
                        </text>
                      </g>
                    ))}

                    {visibleSeries.compound && compoundFillPath && (
                      <path d={compoundFillPath} fill="url(#wlfCompoundGrad)" />
                    )}

                    {visibleSeries.simple && (
                      <path d={simpleLine} fill="none" stroke={SIMPLE_COLOR} strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" />
                    )}

                    {visibleSeries.compound && (
                      <path d={compoundLine} fill="none" stroke={OLIVE} strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round" />
                    )}

                    {simplePts.map((p, i) => (
                      <text key={i} x={p.x} y={MVH - 4} textAnchor="middle" fontSize="11" fill="#bbb">{p.label}</text>
                    ))}

                    {/* Vertical dashed line at hovered column */}
                    {mTooltip && simplePts[mTooltip.idx] && (
                      <line
                        x1={simplePts[mTooltip.idx].x} y1={MPAD.t}
                        x2={simplePts[mTooltip.idx].x} y2={MVH - MPAD.b}
                        stroke="#bbb" strokeWidth="1" strokeDasharray="4,4"
                        pointerEvents="none"
                      />
                    )}

                    {simplePts.map((p, i) => {
                      if (!visibleSeries.simple || !(p.value > 0)) return null;
                      const isActive = mTooltip?.idx === i;
                      return (
                        <g key={`s${i}`} style={{ pointerEvents: 'none' }}>
                          <circle cx={p.x} cy={p.y} r={isActive ? 7 : 5.5}
                            fill={SIMPLE_COLOR} stroke="#fff" strokeWidth="3" />
                        </g>
                      );
                    })}

                    {compoundPts.map((p, i) => {
                      if (!p || !(p.value > 0) || !visibleSeries.compound) return null;
                      const isActive = mTooltip?.idx === i;
                      return (
                        <g key={`c${i}`} style={{ pointerEvents: 'none' }}>
                          <circle cx={p.x} cy={p.y} r={isActive ? 8 : 6.5}
                            fill={OLIVE} stroke="#fff" strokeWidth="3" />
                        </g>
                      );
                    })}

                    {/* Column hover targets — full height, behind nothing (on top) */}
                    {simplePts.map((p, i) => {
                      const prev = simplePts[i - 1];
                      const next = simplePts[i + 1];
                      const leftEdge = prev ? (p.x + prev.x) / 2 : MPAD.l;
                      const rightEdge = next ? (p.x + next.x) / 2 : MVW - MPAD.r;
                      return (
                        <rect
                          key={`col${i}`}
                          x={leftEdge} y={MPAD.t}
                          width={rightEdge - leftEdge}
                          height={MVH - MPAD.t - MPAD.b}
                          fill="transparent"
                          style={{ cursor: 'crosshair' }}
                          onMouseEnter={() => setMTooltip({ idx: i })}
                          onMouseLeave={() => setMTooltip(null)}
                        />
                      );
                    })}
                  </svg>

                  {/* HTML tooltip */}
                  {mTooltip && (() => {
                    const idx = mTooltip.idx;
                    const sp = simplePts[idx];
                    const cp = compoundPts[idx];
                    const period = chartPeriods[idx];
                    const simpleVal = sp?.value ?? 0;
                    const compoundVal = cp?.value ?? 0;

                    const xPct = sp ? (sp.x / MVW * 100) : 0;
                    const topY = Math.min(
                      sp ? sp.y / MVH * 100 : 100,
                      cp ? cp.y / MVH * 100 : 100
                    );
                    const tooltipDate = period?.date
                      ? new Date(period.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : '';
                    const fmt2 = v => Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    const showCompound = visibleSeries.compound && compoundVal > 0;
                    const showSimple = visibleSeries.simple && simpleVal > 0;
                    const visibleTotal = (showCompound ? compoundVal : 0) + (showSimple ? simpleVal : 0);
                    if (!showCompound && !showSimple) return null;
                    const alignRight = xPct > 70;
                    return (
                      <div
                        style={{
                          position: 'absolute',
                          left: `${xPct}%`,
                          top: `${topY}%`,
                          transform: alignRight ? 'translate(-90%, -115%)' : 'translate(-10%, -115%)',
                          background: '#fff',
                          borderRadius: '8px',
                          padding: '10px 14px',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                          border: '1px solid #eee',
                          minWidth: '170px',
                          pointerEvents: 'none',
                          zIndex: 10,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                          <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#ccc', flexShrink: 0 }} />
                          <span style={{ fontSize: '11px', color: '#888' }}>{tooltipDate}</span>
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: 800, color: '#1a1a1a', marginBottom: '8px' }}>
                          USD {fmt2(visibleTotal)}
                        </div>
                        {showCompound && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px', marginBottom: showSimple ? '4px' : 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: OLIVE, flexShrink: 0 }} />
                              <span style={{ fontSize: '12px', color: '#555' }}>Compound</span>
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#1a1a1a' }}>{fmt2(compoundVal)}</span>
                          </div>
                        )}
                        {showSimple && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: SIMPLE_COLOR, flexShrink: 0 }} />
                              <span style={{ fontSize: '12px', color: '#555' }}>Simple</span>
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#1a1a1a' }}>{fmt2(simpleVal)}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Legend — bottom of chart */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginTop: '8px' }}>
                  {[
                    { key: 'compound', label: 'Compound Annually', color: OLIVE },
                    { key: 'simple', label: 'Simple', color: SIMPLE_COLOR },
                  ].map(({ key, label, color }) => (
                    <button key={key} onClick={() => toggleSeries(key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        opacity: visibleSeries[key] ? 1 : 0.35,
                      }}
                    >
                      <span style={{ display: 'inline-block', width: '11px', height: '11px', borderRadius: '2px', background: color, flexShrink: 0 }} />
                      <span style={{ color: '#555', fontSize: '12px', fontWeight: 500 }}>{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Close button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    background: '#D4AE3A', color: '#1a1a1a',
                    border: 'none', padding: '10px 32px',
                    borderRadius: '6px', cursor: 'pointer',
                    fontSize: '14px', fontWeight: '700',
                  }}
                >Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Investments = () => {
  const dispatch = useDispatch();

  const user = useSelector(userInfo);
  const { id, name } = useParams();
  const history = useHistory();

  useEffect(() => {
    const pd = JSON.parse(sessionStorage.getItem('previewDetail'));
    const isClientView = (pd !== null && pd?.sfUserType === 'CL') ||
      (pd === null && user?.sfUserType === 'CL');
    const hasActiveInv = pd !== null ? pd?.hasActiveInvestment : user?.hasActiveInvestment;
    if (isClientView && hasActiveInv === false) {
      history.push(history.location.pathname.startsWith('/preview/') ? '/preview/document' : '/document');
    }
  }, []);

  //获取azure返回的token和objectId
  // const locationState = history.location.search;
  // var obj = Qs.parse(locationState, { ignoreQueryPrefix: true });
  // const name = obj.name;



  const [isButton, setButton] = useState(true);

  const [pageNum, setPageNum] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [listData, setListData] = useState({
    activeProjects: [],
    exitedProjects: [],
    exitingProjects: [],
    booklet: '',
    statement: ''
  });

  const [totalActiveHolding, setTotalActiveHolding] = useState({});
  const [totalActiveLoanHolding, setTotalActiveLoanHolding] = useState({});
  const [totalGrossHolding, setTotalGrossHolding] = useState({});
  const [totalPaymentAmount, setTotalPaymentAmount] = useState({});
  const [totalAccruedInterest, setTotalAccruedInterest] = useState({});
  const [pending, setpending] = useState(true);
  const [representative, setRepresentative] = useState();

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);

  const [hasWLFStatement, setHasWLFStatement] = useState(false);
  const [showWLFModal, setShowWLFModal] = useState(false);
  const [loanInterestData, setLoanInterestData] = useState(null);

  const previewDetail = JSON.parse(sessionStorage.getItem('previewDetail'));

  useEffect(() => {
    if (user.sfUserType === 'CL' && history.location.pathname.includes('preview')) {
      history.push({
        pathname: '/investments'
      });
    }
    else if (user.sfUserType === 'IR') {
      history.push({
        pathname: '/preview/investments'
      });
    }
    else {
      return;
    }
  }, []);

  useEffect(() => {
    isButton ? init('PDLI') : init('LOAN');
  }, [window.localStorage.lang])

  const LAND = () => {
    setButton(true);
    init('PDLI');
  };
  const LOAN = () => {
    setButton(false);
    init('LOAN');
  };
  const formatCurrency = (value) => {
    if (value) {
      return Number(value).toLocaleString()
    } else {
      return 0
    }

  }
  const formatAmountWithC = (value, currency, translateCurency = false, space = true) => {
    if (typeof value !== 'number') return value;
    const mCurency = translateCurency ? currency : currency;
    const formatedValue = value ? Number(value).toLocaleString() : 0;
    const arr = ['$ ', formatedValue, space ? ' ' : '', mCurency];
    return typeof mCurency === 'string' ? arr.join('') : arr;
  };

  const block = () => {
    if (pending === true) {
      setpending(!pending);
    }
  };

  const strip = () => {
    if (pending === false) {
      setpending(!pending);
    }
  };
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const landList = (list, activeTab) => {
    let currutList = [];

    if (activeTab == 0) {
      currutList = list.activeProjects;
    } else if (activeTab == 1) {
      currutList = list.exitingProjects;
    } else {
      currutList = list.exitedProjects;
    }
    return (
      <>
        <div className="mobile-box">
          {
            currutList?.map((item) => (
              <>
                {
                  isButton ? (
                    <div className="moblie-list">
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('Project Name')}</div>
                        <div className="moblie-list-name">
                          {item.projectName}
                        </div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('State / Province')}</div>
                        <div className="moblie-list-name">{item.location}</div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('PurchaseAmount')}</div>
                        <div className="moblie-list-name"> {item.purchaseCurrency === 'CAD' ? t('CAD') : t('USD')}
                          <span className="f-s-20">&nbsp;{formatCurrency(item.purchaseAmount)}&nbsp;</span>
                          {item.purchaseCurrency === 'CAD' ? t('CAD1') : t('USD1')}</div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('LastPurchaseDate')}</div>
                        <div className="moblie-list-name">{item.purchaseDate}</div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('Disposition Status')}</div>
                        <div className="moblie-list-name">{item.dispositionStatus}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="moblie-list">
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('Project Name')}</div>
                        <div className="moblie-list-name">
                          {item.projectName}
                        </div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('State / Province')}</div>
                        <div className="moblie-list-name">{item.location}</div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('OriginalPrincipal')}</div>
                        <div className="moblie-list-name"> {item.purchaseCurrency === 'CAD' ? t('CAD') : t('USD')}
                          <span className="f-s-20">&nbsp;{formatCurrency(item.initialInvestment)}&nbsp;</span>
                          {item.purchaseCurrency === 'CAD' ? t('CAD1') : t('USD1')}</div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('LastPurchaseDate')}</div>
                        <div className="moblie-list-name">{item.purchaseDate}</div>
                      </div>
                      <div className="w-full"><div className="mobile-divider" /></div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('Outstanding Balance')}</div>
                        <div className="moblie-list-name"> {item.purchaseCurrency === 'CAD' ? t('CAD') : t('USD')}
                          <span className="f-s-20">&nbsp;{formatCurrency(item.outstandingBalance)}&nbsp;</span>
                          {item.purchaseCurrency === 'CAD' ? t('CAD1') : t('USD1')}</div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('Latest Interest Cutoff Date')}</div>
                        <div className="moblie-list-name">{item.paymentDate}</div>
                      </div>
                      <div className="mobile-gold-section">
                        <div className="mobile-item">
                          <div className="moblie-list-title">{t('TotalDistribution')}</div>
                          <div className="moblie-list-name">
                            {item.purchaseCurrdistributionCurrencyency === 'CAD' ? t('CAD') : t('USD')}
                            <span className="f-s-20">&nbsp;{formatCurrency(item.distributionAmount)}&nbsp;</span>
                            {item.distributionCurrency === 'CAD' ? t('CAD1') : t('USD1')}
                          </div>
                        </div>
                        {item.accruedInterest > 0 && (
                          <div className="mobile-item">
                            <div className="moblie-list-title">{t('Accrued Interest')}</div>
                            <div className="moblie-list-name">
                              {t('USD')}&nbsp;{formatCurrency(item.accruedInterest)}{t('USD1')}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                }
              </>
            ))
          }
        </div>
        <Table className="table-box  align-middle">
          <thead>
            <tr>
              {isButton ? (
                <>
                  <th>{t('Project Name')}</th>
                  <th>{t('State / Province')}</th>
                  <th>{t('PurchaseAmount')}</th>
                  {
                    activeTab === 2 ? <th>{t('Gross Distribution')}</th> : ""
                  }
                  <th>{t('LastPurchaseDate')}</th>
                  <th>{t('Disposition Status')}</th>
                </>
              ) : (
                <>
                  <th>{t('Project Name')}</th>
                  <th>{t('State / Province')}</th>
                  <th>{t('OriginalPrincipal')}</th>
                  <th>{t('LastPurchaseDate')}</th>
                  <th>{t('Outstanding Balance')}</th>
                  <th>{t('Latest Interest Cutoff Date')}</th>
                  <th>{t('TotalDistribution')}</th>
                  <th>{t('Accrued Interest')}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {currutList?.map((item) => (
              <tr>
                {
                  isButton ? (
                    <>

                      <td className="name">{item.projectName}</td>
                      <td className="place">{item.location}</td>

                      <td className="amount">
                        {/* {item.purchaseCurrency}&nbsp;&nbsp; */}
                        {item.purchaseCurrency === 'CAD' ? t('CAD') : t('USD')}
                        <span className="f-s-20">&nbsp;{formatCurrency(item.purchaseAmount)}&nbsp;</span>
                        {item.purchaseCurrency === 'CAD' ? t('CAD1') : t('USD1')}
                      </td>
                      {
                        activeTab === 2 ? (<>
                          <td style={{ textAlign: window.localStorage.lang === 'jp' ? 'center' : '' }}>
                            {
                              item.grossDistribution !== 0 ? (<>
                                {item.purchaseCurrency === 'CAD' ? t('CAD') : t('USD')}
                                <span className="f-s-20">&nbsp;{formatCurrency(item.grossDistribution)}&nbsp;</span>
                                {item.purchaseCurrency === 'CAD' ? t('CAD1') : t('USD1')}
                              </>) : ""
                            }
                          </td>
                        </>) : ""
                      }
                      <td className="date">{item.purchaseDate}</td>
                      <td className="status">{item.dispositionStatus}</td>
                    </>
                  ) : (
                    <>
                      <td className="name">{item.projectName}
                      </td>
                      <td className="place">{item.location}</td>
                      <td className="amount">
                        {/* {item.purchaseCurrency + ' '} */}
                        {item.purchaseCurrency === 'CAD' ? t('CAD') : t('USD')}
                        <span className="f-s-20">&nbsp;{formatCurrency(item.initialInvestment)}&nbsp;</span>
                        {item.purchaseCurrency === 'CAD' ? t('CAD1') : t('USD1')}
                      </td>
                      <td className="date">{item.purchaseDate}</td>
                      <td className="status">
                        {/* {(formatAmountWithC(item.purchaseAmount, item.purchaseCurrency, true))} */}
                        {item.purchaseCurrency === 'CAD' ? t('CAD') : t('USD')}
                        <span className="f-s-20">&nbsp;{formatCurrency(item.outstandingBalance)}&nbsp;</span>
                        {item.purchaseCurrency === 'CAD' ? t('CAD1') : t('USD1')}
                        {/* formatCurrency */}
                      </td>
                      <td className="status">{item.paymentDate}</td>
                      <td className="status">
                        {item.distributionCurrency === 'CAD' ? t('CAD') : t('USD')}
                        &nbsp;{formatCurrency(item.distributionAmount)}
                        {item.distributionCurrency === 'CAD' ? t('CAD1') : t('USD1')}
                      </td>
                      <td className="status">
                        {
                          item.accruedInterest > 0 && (<>
                            {t('USD')}
                            &nbsp;{formatCurrency(item.accruedInterest)}
                            {t('USD1')}
                          </>)
                        }
                      </td>
                    </>
                  )}
              </tr>
            ))}
          </tbody>
        </Table>
      </>
    );
  };

  const landCard = (list, activeTab) => {
    let currutList = [];

    if (activeTab == 0) {
      currutList = list.activeProjects;
    } else if (activeTab == 1) {
      currutList = list.exitingProjects;
    } else {
      currutList = list.exitedProjects;
    }
    return (
      <>
        <div className="mobile-box">
          {
            currutList?.map((item) => (
              <>
                {
                  isButton ? (
                    <div className="moblie-list">
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('Project Name')}</div>
                        <div className="moblie-list-name">{item.projectName}</div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('State / Province')}</div>
                        <div className="moblie-list-name">{item.location}</div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('PurchaseAmount')}</div>
                        <div className="moblie-list-name">
                          {item.purchaseCurrency === 'CAD' ? t('CAD') : t('USD')}
                          <span className="f-s-20">&nbsp;{formatCurrency(item.purchaseAmount)}&nbsp;</span>
                          {item.purchaseCurrency === 'CAD' ? t('CAD1') : t('USD1')}
                        </div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('LastPurchaseDate')}</div>
                        <div className="moblie-list-name">{item.purchaseDate}</div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('Disposition Status')}</div>
                        <div className="moblie-list-name">{item.dispositionStatus}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="moblie-list">
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('Project Name')}</div>
                        <div className="moblie-list-name">{item.projectName}</div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('State / Province')}</div>
                        <div className="moblie-list-name">{item.location}</div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('OriginalPrincipal')}</div>
                        <div className="moblie-list-name">
                          {item.purchaseCurrency === 'CAD' ? t('CAD') : t('USD')}
                          <span className="f-s-20">&nbsp;{formatCurrency(item.initialInvestment)}&nbsp;</span>
                          {item.purchaseCurrency === 'CAD' ? t('CAD1') : t('USD1')}</div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('LastPurchaseDate')}</div>
                        <div className="moblie-list-name">{item.purchaseDate}</div>
                      </div>
                      <div className="w-full"><div className="mobile-divider" /></div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('Outstanding Balance')}</div>
                        <div className="moblie-list-name"> {item.purchaseCurrency === 'CAD' ? t('CAD') : t('USD')}
                          <span className="f-s-20">&nbsp;{formatCurrency(item.outstandingBalance)}&nbsp;</span>
                          {item.purchaseCurrency === 'CAD' ? t('CAD1') : t('USD1')}</div>
                      </div>
                      <div className="mobile-item">
                        <div className="moblie-list-title">{t('Latest Interest Cutoff Date')}</div>
                        <div className="moblie-list-name">{item.paymentDate}</div>
                      </div>
                      <div className="mobile-gold-section">
                        <div className="mobile-item">
                          <div className="moblie-list-title">{t('TotalDistribution')}</div>
                          <div className="moblie-list-name">
                            {item.distributionCurrency === 'CAD' ? t('CAD') : t('USD')}
                            &nbsp;{formatCurrency(item.distributionAmount)}
                            {item.distributionCurrency === 'CAD' ? t('CAD1') : t('USD1')}
                          </div>
                        </div>
                        {item.accruedInterest > 0 && (
                          <div className="mobile-item">
                            <div className="moblie-list-title">{t('Accrued Interest')}</div>
                            <div className="moblie-list-name">
                              {t('USD')}&nbsp;{formatCurrency(item.accruedInterest)}{t('USD1')}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                }
              </>
            ))
          }
        </div>
        <div className="list-box">
          {currutList?.map((item) => (
            <>
              {isButton ? (
                <div className="list">
                  <div className="list-label">
                    <span className="name row">{item.projectName}</span>
                    <span className="location row">{item.location}</span>
                  </div>
                  <div className="list-padding">
                    <div className='col-md-12 row'>
                      <div className='col-md-6'>
                        <div className='col-md-6 list-title'>
                          <span className='list-title'>{t('PurchaseAmount')}</span>
                        </div>
                        <div className='col-md-6'>
                          <p className="amount">
                            {item.purchaseCurrency === 'CAD' ? t('CAD') : t('USD')}
                            {/* {item.purchaseCurrency}&nbsp;&nbsp; */}
                            <span className="f-s-20">&nbsp;{formatCurrency(item.purchaseAmount)}&nbsp;</span>
                            {item.purchaseCurrency === 'CAD' ? t('CAD1') : t('USD1')}
                          </p>
                        </div>
                      </div>
                      {
                        activeTab === 2 ? (<>
                          <div className='col-md-6'>
                            <div className='col-md-6 list-title'>
                              <span className='list-title'>{t('Gross Distribution')}</span>
                            </div>
                            <div className='col-md-6'>
                              {
                                item.grossDistribution !== 0 ? (<>
                                  <p className="amount">
                                    {item.purchaseCurrency === 'CAD' ? t('CAD') : t('USD')}
                                    <span className="f-s-20">&nbsp;{formatCurrency(item.grossDistribution)}&nbsp;</span>
                                    {item.purchaseCurrency === 'CAD' ? t('CAD1') : t('USD1')}
                                  </p>
                                </>) : ""
                              }
                            </div>
                          </div>
                        </>) : ""
                      }
                    </div>
                    <div className="loan-divider" />
                    <div className='col-md-12 row'>
                      <div className='col-md-6' >
                        <span className='list-title'>{t('Purchase Date')}</span>
                        <p className="date">{item.purchaseDate}</p>
                      </div>
                      <div className='col-md-6'>
                        <span className='list-title'>{t('Disposition Status')}</span>
                        <p className="status">{item.dispositionStatus}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="list-loan">
                  <div className="list-label">
                    <span className="name row">
                      {item.projectName}
                    </span>
                    <span className="location row">{item.location}</span>
                  </div>

                  <div className="list-padding">
                    <div className='col-md-12 row walton-gold-bg'>
                      <div className='col-md-6'>
                        <span className='list-title'>{t('TotalDistribution')}</span>
                        <p className="date">{item.distributionCurrency === 'CAD' ? t('CAD') : t('USD')}
                          &nbsp;{formatCurrency(item.distributionAmount)}
                          {item.distributionCurrency === 'CAD' ? t('CAD1') : t('USD1')}</p>
                      </div>
                      {
                        item.accruedInterest > 0 && (<>
                          <div className='col-md-6'>
                            <span className='list-title'>{t('Accrued Interest')}</span>
                            <p className="date">{t('USD')}
                              &nbsp;{formatCurrency(item.accruedInterest)}
                              {t('USD1')}</p>
                          </div>
                        </>)
                      }
                    </div>

                    <div className='col-md-12 row padding-20'>
                      <div className='col-md-6' >
                        <span className='list-title'>{t('OriginalPrincipal')}</span>
                        <p className="amount">
                          {item.purchaseCurrency === 'CAD' ? t('CAD') : t('USD')}
                          <span className="f-s-20">&nbsp;{formatCurrency(item.initialInvestment)}&nbsp;</span>
                          {item.purchaseCurrency === 'CAD' ? t('CAD1') : t('USD1')}
                        </p>

                      </div>
                      <div className='col-md-6'>
                        <span className='list-title'>{t('LastPurchaseDate')}</span>
                        <p className="date">{item.purchaseDate}</p>
                      </div>
                    </div>
                    <div className="loan-divider" />
                    <div className='col-md-12 row padding-20'>
                      <div className='col-md-6' >
                        <span className='list-title'>{t('Outstanding Balance')}</span>
                        <p className="status">
                          {item.purchaseCurrency === 'CAD' ? t('CAD') : t('USD')}
                          <span className="f-s-20">&nbsp;{formatCurrency(item.outstandingBalance)}&nbsp;</span>
                          {item.purchaseCurrency === 'CAD' ? t('CAD1') : t('USD1')}
                        </p>
                      </div>
                      <div className='col-md-6'>
                        <span className='list-title'>{t('Latest Interest Cutoff Date')}</span>
                        <p className="amount">{item.paymentDate}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ))}
        </div>
      </>
    );
  };

  const tabLANDObj = [
    { name: t('Pending Offer'), key: 0 },
    { name: t('Offer Received'), key: 1 },
    { name: t('Exits & Closings'), key: 2 },
  ];

  const tabLAODObj = [
    { name: t('Active'), key: 0 },
    { name: t('Offer Received'), key: 1 },
    { name: t('Exits & Closings'), key: 2 },
  ];

  const getTotal = () => {
    let currutList = [
      listData.activeProjects?.length,
      listData.exitingProjects?.length,
      listData.exitedProjects?.length,
    ];

    return currutList;
  };

  const landpage = (tabObj) => {
    let total = getTotal();
    return (
      <div>
        {tabObj.map((item, index) => {
          return (
            <div className="pending-box">
              <div className="Pending d-flex justify-content-between">
                <div
                  onClick={() => {
                    setActiveTab(item.key);
                  }}
                >
                  <p className='left-border'>
                    {item.name} ({total[index]})
                  </p>
                </div>
                {index == 0 ? (
                  <div className="d-flex togge-table">
                    <div style={{ cursor: "pointer" }} onClick={() => block()}>
                      {pending ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M5 4C4.44772 4 4 4.44772 4 5V10C4 10.5523 4.44772 11 5 11H10C10.5523 11 11 10.5523 11 10V5C11 4.44772 10.5523 4 10 4H5ZM14 4C13.4477 4 13 4.44772 13 5V10C13 10.5523 13.4477 11 14 11H19C19.5523 11 20 10.5523 20 10V5C20 4.44772 19.5523 4 19 4H14ZM4 14C4 13.4477 4.44772 13 5 13H10C10.5523 13 11 13.4477 11 14V19C11 19.5523 10.5523 20 10 20H5C4.44772 20 4 19.5523 4 19V14ZM14 13C13.4477 13 13 13.4477 13 14V19C13 19.5523 13.4477 20 14 20H19C19.5523 20 20 19.5523 20 19V14C20 13.4477 19.5523 13 19 13H14Z"
                            fill="#9B9B9B"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M5 4C4.44772 4 4 4.44772 4 5V10C4 10.5523 4.44772 11 5 11H10C10.5523 11 11 10.5523 11 10V5C11 4.44772 10.5523 4 10 4H5ZM14 4C13.4477 4 13 4.44772 13 5V10C13 10.5523 13.4477 11 14 11H19C19.5523 11 20 10.5523 20 10V5C20 4.44772 19.5523 4 19 4H14ZM4 14C4 13.4477 4.44772 13 5 13H10C10.5523 13 11 13.4477 11 14V19C11 19.5523 10.5523 20 10 20H5C4.44772 20 4 19.5523 4 19V14ZM14 13C13.4477 13 13 13.4477 13 14V19C13 19.5523 13.4477 20 14 20H19C19.5523 20 20 19.5523 20 19V14C20 13.4477 19.5523 13 19 13H14Z"
                            fill="#003448"
                          />
                        </svg>
                      )}
                    </div>
                    <div style={{ cursor: "pointer" }} onClick={() => strip()}>
                      {pending ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M4 5.5C4 4.67157 4.67157 4 5.5 4H18.5C19.3284 4 20 4.67157 20 5.5C20 6.32843 19.3284 7 18.5 7H5.5C4.67157 7 4 6.32843 4 5.5ZM4 12C4 11.1716 4.67157 10.5 5.5 10.5H18.5C19.3284 10.5 20 11.1716 20 12C20 12.8284 19.3284 13.5 18.5 13.5H5.5C4.67157 13.5 4 12.8284 4 12ZM5.5 17C4.67157 17 4 17.6716 4 18.5C4 19.3284 4.67157 20 5.5 20H18.5C19.3284 20 20 19.3284 20 18.5C20 17.6716 19.3284 17 18.5 17H5.5Z"
                            fill="#003448"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M4 5.5C4 4.67157 4.67157 4 5.5 4H18.5C19.3284 4 20 4.67157 20 5.5C20 6.32843 19.3284 7 18.5 7H5.5C4.67157 7 4 6.32843 4 5.5ZM4 12C4 11.1716 4.67157 10.5 5.5 10.5H18.5C19.3284 10.5 20 11.1716 20 12C20 12.8284 19.3284 13.5 18.5 13.5H5.5C4.67157 13.5 4 12.8284 4 12ZM5.5 17C4.67157 17 4 17.6716 4 18.5C4 19.3284 4.67157 20 5.5 20H18.5C19.3284 20 20 19.3284 20 18.5C20 17.6716 19.3284 17 18.5 17H5.5Z"
                            fill="#9B9B9B"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                ) : (
                  <></>
                )}
              </div>
              {activeTab == item.key ? (
                <div>{pending ? landList(listData, activeTab) : landCard(listData, activeTab)}</div>
              ) : (
                <></>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const checkWLFStatement = async () => {
    let params = {
      accessToken: user.accessToken,
      userId: user.userId,
      clientId: user.sfUserType === 'IR' ? previewDetail?.clientId : id ? id : user.clientID
    };
    const data = await get('/investments/hasWLF', params);
    setHasWLFStatement(data);
  }

  const fetchLoanInterestDetail = async () => {
    const accountNum = user.sfUserType === 'IR' ? previewDetail?.clientId : id ? id : user.clientID;
    if (!accountNum) return;
    const data = await get('/investments/loanInterestDetail', {
      accessToken: user.accessToken,
      userId: user.userId,
      accountNum,
    });
    console.log(data);
    if (data) setLoanInterestData(data);
  };

  const init = async (tab) => {
    if (id) {
      let params = {
        accessToken: user.accessToken,
        userId: user.userId
      };
      let url = '/businessPartner/client/' + id + '/investments/' + tab;
      const data = await get(url, params);
      if (data) {
        if (data.activeProjects.length > 0) {
          for (let i = 0; i < data.activeProjects.length; i++) {
            var day = data.activeProjects[i].purchaseDate.slice(8, 10)
            var year = data.activeProjects[i].purchaseDate.slice(0, 4)
            var month = data.activeProjects[i].purchaseDate.slice(5, 7)
            data.activeProjects[i].purchaseDate = year + ' / ' + month + ' / ' + day

            var day = data.activeProjects[i].paymentDate.slice(8, 10)
            var year = data.activeProjects[i].paymentDate.slice(0, 4)
            var month = data.activeProjects[i].paymentDate.slice(5, 7)
            if (data.activeProjects[i].paymentDate) {
              data.activeProjects[i].paymentDate = year + ' / ' + month + ' / ' + day
            }
          }
        }

        if (data.exitedProjects.length > 0) {
          for (let i = 0; i < data.exitedProjects.length; i++) {
            var day = data.exitedProjects[i].purchaseDate.slice(8, 10)
            var year = data.exitedProjects[i].purchaseDate.slice(0, 4)
            var month = data.exitedProjects[i].purchaseDate.slice(5, 7)
            data.exitedProjects[i].purchaseDate = year + ' / ' + month + ' / ' + day

            var day = data.exitedProjects[i].paymentDate.slice(8, 10)
            var year = data.exitedProjects[i].paymentDate.slice(0, 4)
            var month = data.exitedProjects[i].paymentDate.slice(5, 7)
            if (data.exitedProjects[i].paymentDate) {
              data.exitedProjects[i].paymentDate = year + ' / ' + month + ' / ' + day
            }
          }
        }
        if (data.exitingProjects.length > 0) {
          for (let i = 0; i < data.exitingProjects.length; i++) {
            var day = data.exitingProjects[i].purchaseDate.slice(8, 10)
            var year = data.exitingProjects[i].purchaseDate.slice(0, 4)
            var month = data.exitingProjects[i].purchaseDate.slice(5, 7)
            data.exitingProjects[i].purchaseDate = year + ' / ' + month + ' / ' + day

            var day = data.exitingProjects[i].paymentDate.slice(8, 10)
            var year = data.exitingProjects[i].paymentDate.slice(0, 4)
            var month = data.exitingProjects[i].paymentDate.slice(5, 7)
            if (data.exitingProjects[i].paymentDate) {
              data.exitingProjects[i].paymentDate = year + ' / ' + month + ' / ' + day
            }
          }
        }
        setListData({
          activeProjects: data.activeProjects != '' ? data.activeProjects : [],
          exitedProjects: data.exitedProjects != '' ? data.exitedProjects : [],
          exitingProjects: data.exitingProjects != '' ? data.exitingProjects : [],
          booklet: data?.booklet,
          statement: data?.statement
        });
        setTotalActiveHolding(data.totalInvestmentAmount);
        setTotalActiveLoanHolding(data.totalLoanAmount);
        setTotalPaymentAmount(data.totalPaymentAmount);
        setTotalAccruedInterest(data.totalAccruedInterest);
        setRepresentative(data.representative)

      }
    }
    else {
      let params = {
        accessToken: user.accessToken,
        userId: user.userId,
      };
      let url = '/investments/' + tab;
      const data = await get(url, params);
      if (data) {
        if (data.activeProjects.length > 0) {
          for (let i = 0; i < data.activeProjects.length; i++) {
            var day = data.activeProjects[i].purchaseDate.slice(8, 10)
            var year = data.activeProjects[i].purchaseDate.slice(0, 4)
            var month = data.activeProjects[i].purchaseDate.slice(5, 7)
            data.activeProjects[i].purchaseDate = year + ' / ' + month + ' / ' + day

            var day = data.activeProjects[i].paymentDate.slice(8, 10)
            var year = data.activeProjects[i].paymentDate.slice(0, 4)
            var month = data.activeProjects[i].paymentDate.slice(5, 7)
            if (data.activeProjects[i].paymentDate) {
              data.activeProjects[i].paymentDate = year + ' / ' + month + ' / ' + day
            }
          }
        }

        if (data.exitedProjects.length > 0) {
          for (let i = 0; i < data.exitedProjects.length; i++) {
            var day = data.exitedProjects[i].purchaseDate.slice(8, 10)
            var year = data.exitedProjects[i].purchaseDate.slice(0, 4)
            var month = data.exitedProjects[i].purchaseDate.slice(5, 7)
            data.exitedProjects[i].purchaseDate = year + ' / ' + month + ' / ' + day

            var day = data.exitedProjects[i].paymentDate.slice(8, 10)
            var year = data.exitedProjects[i].paymentDate.slice(0, 4)
            var month = data.exitedProjects[i].paymentDate.slice(5, 7)
            if (data.exitedProjects[i].paymentDate) {
              data.exitedProjects[i].paymentDate = year + ' / ' + month + ' / ' + day
            }
          }
        }

        if (data.exitingProjects.length > 0) {
          for (let i = 0; i < data.exitingProjects.length; i++) {
            var day = data.exitingProjects[i].purchaseDate.slice(8, 10)
            var year = data.exitingProjects[i].purchaseDate.slice(0, 4)
            var month = data.exitingProjects[i].purchaseDate.slice(5, 7)
            data.exitingProjects[i].purchaseDate = year + ' / ' + month + ' / ' + day
            var day = data.exitingProjects[i].paymentDate.slice(8, 10)
            var year = data.exitingProjects[i].paymentDate.slice(0, 4)
            var month = data.exitingProjects[i].paymentDate.slice(5, 7)
            if (data.exitingProjects[i].paymentDate) {
              data.exitingProjects[i].paymentDate = year + ' / ' + month + ' / ' + day
            }
          }
        }

        setListData({
          activeProjects: data.activeProjects != '' ? data.activeProjects : [],
          exitedProjects: data.exitedProjects != '' ? data.exitedProjects : [],
          exitingProjects: data.exitingProjects != '' ? data.exitingProjects : [],
          booklet: data?.booklet,
          statement: data?.statement
        });
        setTotalActiveHolding(data.totalInvestmentAmount);
        setTotalActiveLoanHolding(data.totalLoanAmount);
        setTotalPaymentAmount(data.totalPaymentAmount);
        setTotalAccruedInterest(data.totalAccruedInterest);
        setRepresentative(data.representative)
      }
    }
  };


  const [datetime, setDatetime] = useState('');

  const getdate = () => {
    var time = new Date();
    var year = time.getFullYear();
    // var month = time.getMonth(); // 月份从0开始，因此需要加1
    var month = time.getMonth() + 1 < 10 ? "0" + (time.getMonth() + 1) : time.getMonth() + 1;
    var day = time.getDate() < 10 ? "0" + time.getDate() : time.getDate();
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    //console.log(months[month])
    // setDatetime(months[month] + ' ' + day + ', ' + year)
    setDatetime(year + '/' + month + '/' + day)
  }
  const downloadFile = async (apiUrl, fileName, clientID) => {
    setLoading(true)
    axios.get(process.env.REACT_APP_BASEPATH + apiUrl, {
      responseType: 'blob',
      headers: {
        accessToken: user.accessToken,
        userId: user.userId,
        clientID: clientID
      }
    }).then((res) => {
      if (res.data.size === 0) { return }
      setLoading(false)
      const blob = new Blob([res.data], {
        type: 'application/pdf'
      })
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a'); // 创建a标签
      link.href = objectUrl;
      link.download = fileName;
      link.click(); // 下载文件
      URL.revokeObjectURL(objectUrl);

    }).catch((err) => {
      console.log('---err---', err)
    })
  }
  const goto = () => {

    history.push({
      pathname: '/viewClient',
    });
  }
  useEffect(() => {
    if (user.accessToken && user.userId) {
      init('PDLI');
      getdate();
      checkWLFStatement();
      fetchLoanInterestDetail();
    }
  }, []);
  return (
    <div className="investments">
      {loading ? <Loading /> : null}

      <div className="main-box">
        {
          user.sfUserType === 'CL' ? (<>
            <div className='row'>
              <ExitBanner isMissingProfileBannerOpen={true} isUpdateProfileButtonVisible={true} />
            </div> </>) : null
        }
        <div className="headline">
          {
            id || name ? (
              <>
                < div className='back-button pointer d-flex' onClick={goto}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M16.1939 18.3047C16.3142 18.425 16.377 18.5777 16.377 18.7488C16.377 18.9198 16.3142 19.0726 16.1939 19.1929C16.0739 19.3129 15.9225 19.376 15.7534 19.376C15.5844 19.376 15.433 19.3129 15.313 19.1929L8.56004 12.44C8.44005 12.32 8.37695 12.1685 8.37695 11.9995C8.37695 11.8305 8.44005 11.6791 8.56004 11.5591L15.3016 4.81828C15.363 4.74816 15.438 4.69582 15.5242 4.6641C15.5981 4.63684 15.675 4.62305 15.7534 4.62305C15.8318 4.62305 15.9087 4.63684 15.9827 4.6641C16.0688 4.69582 16.1439 4.74816 16.1939 4.80613C16.3139 4.92613 16.377 5.07755 16.377 5.24658C16.377 5.41561 16.3139 5.56704 16.194 5.68693L9.88861 11.9994L16.1939 18.3047Z" fill="#5582A0" />
                  </svg>
                  <p className="back-text">{t('View Clients')}</p>
                </div>
              </>
            ) : (<></>)
          }
          <p>{t('Investments')}</p>
        </div>
        <div className="statement-box investment-header">
          <div className='investment-text row'>
            <div className='col-md-4' style={{ textAlign: 'left' }}>
              <div className="name">
                {
                  user.sfUserType === 'IR' ? <p>{previewDetail?.fullName}</p> : <p>{id ? name : user.fullName}</p>
                }
              </div>
              <div className="representative">
                <span>{t('Walton Representative')}: {representative}</span>
              </div>
            </div>
            <div className='col-md-4'>
              <div className="id">
                {
                  user.sfUserType === 'IR' ? <span>{t('Client ID')}: {previewDetail?.clientId}</span> : <span>{t('Client ID')}: {id ? id : user.clientID}</span>
                }
              </div>
            </div>
            <div className='col-md-4'>
              <div className="investments-button row">
                <button
                  type="button"
                  class="download-btn"
                  disable
                  onClick={() => {
                    if (user.sfUserType == 'CL' || user.sfUserType === 'IR') {
                      downloadFile('/appUser/booklet', user.bookletFileName)
                    } else {
                      downloadFile(`/businessPartner/client/${id}/booklet`, user.bookletFileName, id)
                    }
                    // window.open(listData.booklet);
                  }}
                >
                  {t('Latest Project Update')}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M12.4167 4C12.7964 4 13.1102 4.28215 13.1598 4.64823L13.1667 4.75V13.9385L16.553 10.553C16.8193 10.2867 17.2359 10.2625 17.5295 10.4804L17.6137 10.553C17.8799 10.8193 17.9041 11.2359 17.6863 11.5295L17.6137 11.6137L12.947 16.2803C12.6807 16.5466 12.2641 16.5708 11.9705 16.3529L11.8863 16.2803L7.21967 11.6137C6.92678 11.3208 6.92678 10.8459 7.21967 10.553C7.48594 10.2867 7.9026 10.2625 8.19621 10.4804L8.28033 10.553L11.6667 13.9396V4.75C11.6667 4.33579 12.0025 4 12.4167 4ZM19.8265 17.3149C19.7768 16.9488 19.463 16.6667 19.0833 16.6667L18.9816 16.6735C18.6155 16.7232 18.3333 17.037 18.3333 17.4167L18.333 18H6.5V17.4167L6.49315 17.3149C6.44349 16.9488 6.1297 16.6667 5.75 16.6667C5.33579 16.6667 5 17.0025 5 17.4167V18.75L5.00685 18.8518C5.05651 19.2178 5.3703 19.5 5.75 19.5H19.0833L19.1851 19.4932C19.5512 19.4435 19.8333 19.1297 19.8333 18.75V17.4167L19.8265 17.3149Z"
                      fill="#003448"
                    />
                  </svg>
                </button>
              </div>
              <div className="statement investments-button row">
                <button
                  type="button"
                  class="download-btn"
                  disable
                  onClick={() => {
                    if (user.sfUserType == 'CL' || user.sfUserType === 'IR') {
                      downloadFile('/appUser/statement', user.statementFileName)
                    } else {
                      downloadFile(`/businessPartner/client/${id}/statement`, user.statementFileName, id)
                    }
                    // window.open(listData.statement);
                  }}
                >
                  {t('Investor Statement')}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M12.4167 4C12.7964 4 13.1102 4.28215 13.1598 4.64823L13.1667 4.75V13.9385L16.553 10.553C16.8193 10.2867 17.2359 10.2625 17.5295 10.4804L17.6137 10.553C17.8799 10.8193 17.9041 11.2359 17.6863 11.5295L17.6137 11.6137L12.947 16.2803C12.6807 16.5466 12.2641 16.5708 11.9705 16.3529L11.8863 16.2803L7.21967 11.6137C6.92678 11.3208 6.92678 10.8459 7.21967 10.553C7.48594 10.2867 7.9026 10.2625 8.19621 10.4804L8.28033 10.553L11.6667 13.9396V4.75C11.6667 4.33579 12.0025 4 12.4167 4ZM19.8265 17.3149C19.7768 16.9488 19.463 16.6667 19.0833 16.6667L18.9816 16.6735C18.6155 16.7232 18.3333 17.037 18.3333 17.4167L18.333 18H6.5V17.4167L6.49315 17.3149C6.44349 16.9488 6.1297 16.6667 5.75 16.6667C5.33579 16.6667 5 17.0025 5 17.4167V18.75L5.00685 18.8518C5.05651 19.2178 5.3703 19.5 5.75 19.5H19.0833L19.1851 19.4932C19.5512 19.4435 19.8333 19.1297 19.8333 18.75V17.4167L19.8265 17.3149Z"
                      fill="#003448"
                    />
                  </svg>
                </button>
              </div>
              {
                hasWLFStatement && (
                  <div className="wlf investments-button row">
                    <button
                      type="button"
                      class="download-btn"
                      disable
                      onClick={() => {
                        if (user.sfUserType === 'CL' || user.sfUserType === 'IR') {
                          downloadFile('/appUser/wlf', 'WLF Statement.pdf');
                        } else {
                          downloadFile(`/businessPartner/client/${id}/wlf`, 'WLF Statement.pdf', id)
                        }
                      }}
                    >
                      {t('WLF Statement')}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M12.4167 4C12.7964 4 13.1102 4.28215 13.1598 4.64823L13.1667 4.75V13.9385L16.553 10.553C16.8193 10.2867 17.2359 10.2625 17.5295 10.4804L17.6137 10.553C17.8799 10.8193 17.9041 11.2359 17.6863 11.5295L17.6137 11.6137L12.947 16.2803C12.6807 16.5466 12.2641 16.5708 11.9705 16.3529L11.8863 16.2803L7.21967 11.6137C6.92678 11.3208 6.92678 10.8459 7.21967 10.553C7.48594 10.2867 7.9026 10.2625 8.19621 10.4804L8.28033 10.553L11.6667 13.9396V4.75C11.6667 4.33579 12.0025 4 12.4167 4ZM19.8265 17.3149C19.7768 16.9488 19.463 16.6667 19.0833 16.6667L18.9816 16.6735C18.6155 16.7232 18.3333 17.037 18.3333 17.4167L18.333 18H6.5V17.4167L6.49315 17.3149C6.44349 16.9488 6.1297 16.6667 5.75 16.6667C5.33579 16.6667 5 17.0025 5 17.4167V18.75L5.00685 18.8518C5.05651 19.2178 5.3703 19.5 5.75 19.5H19.0833L19.1851 19.4932C19.5512 19.4435 19.8333 19.1297 19.8333 18.75V17.4167L19.8265 17.3149Z"
                          fill="#003448"
                        />
                      </svg>
                    </button>
                  </div>
                )
              }
            </div>
          </div>
          {/* <div className='investment-header'>
          </div> */}
        </div>
        <div className="tabs d-flex align-items-center pointer userSelect">
          <div className={isButton ? 'tabsName checked' : 'tabsName'} onClick={() => LAND()}>
            <p>{t('LAND')}</p>
          </div>
          <div className={!isButton ? 'tabsName checked' : 'tabsName'} onClick={() => LOAN()}>
            <p>{t('LOAN')}</p>
          </div>
        </div>
        {isButton ? (
          <div className="tabsContent">
            <div className="total-box">
              <div className="time d-flex align-items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="date-icon"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M8.00016 1.33203C4.32394 1.33203 1.3335 4.32247 1.3335 7.9987C1.3335 11.6749 4.32394 14.6654 8.00016 14.6654C11.6764 14.6654 14.6668 11.6749 14.6668 7.9987C14.6668 4.32247 11.6764 1.33203 8.00016 1.33203ZM8.00016 13.6326C4.89404 13.6326 2.36628 11.1048 2.36628 7.9987C2.36628 4.89258 4.89404 2.36482 8.00016 2.36482C11.1068 2.36482 13.634 4.89258 13.634 7.9987C13.634 11.1048 11.1063 13.6326 8.00016 13.6326ZM8.51654 7.7818L10.3756 9.17607C10.6038 9.347 10.6503 9.67078 10.4788 9.89854C10.3776 10.0349 10.2227 10.1056 10.0652 10.1056C9.95727 10.1056 9.84881 10.0721 9.75587 10.0023L7.69029 8.45315C7.56016 8.35607 7.48373 8.20268 7.48373 8.04003V4.94164C7.48373 4.65607 7.71456 4.42523 8.00014 4.42523C8.28571 4.42523 8.51654 4.65607 8.51654 4.94164V7.7818Z"
                    fill="#003448"
                  />
                </svg>
                {t('As of')} {datetime}
              </div>
              <div className="total total-bg1">
                <p>{t('Total Active Holding')}</p>
                <div className="amount-box d-flex justify-content-center">
                  <div className="vertical">
                    <div className="amount amount-left">
                      {t('USD')}
                      <span>{formatCurrency(totalActiveHolding?.USD)}&nbsp;</span>
                      {t('USD1')}
                      <br />
                    </div>
                    <div className="amount amount-right">
                      {t('CAD')}
                      <span>{formatCurrency(totalActiveHolding?.CAD)}&nbsp;</span>
                      {t('CAD1')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {landpage(tabLANDObj)}
          </div>
        ) : (
          <div className="tabsContent">
            <div className="total-box loan-tab">
              <div className="time d-flex align-items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className="date-icon"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M8.00016 1.33203C4.32394 1.33203 1.3335 4.32247 1.3335 7.9987C1.3335 11.6749 4.32394 14.6654 8.00016 14.6654C11.6764 14.6654 14.6668 11.6749 14.6668 7.9987C14.6668 4.32247 11.6764 1.33203 8.00016 1.33203ZM8.00016 13.6326C4.89404 13.6326 2.36628 11.1048 2.36628 7.9987C2.36628 4.89258 4.89404 2.36482 8.00016 2.36482C11.1068 2.36482 13.634 4.89258 13.634 7.9987C13.634 11.1048 11.1063 13.6326 8.00016 13.6326ZM8.51654 7.7818L10.3756 9.17607C10.6038 9.347 10.6503 9.67078 10.4788 9.89854C10.3776 10.0349 10.2227 10.1056 10.0652 10.1056C9.95727 10.1056 9.84881 10.0721 9.75587 10.0023L7.69029 8.45315C7.56016 8.35607 7.48373 8.20268 7.48373 8.04003V4.94164C7.48373 4.65607 7.71456 4.42523 8.00014 4.42523C8.28571 4.42523 8.51654 4.65607 8.51654 4.94164V7.7818Z"
                    fill="#003448"
                  />
                </svg>
                {t('As of')} {datetime}
              </div>
              <div className='total-wrapper'>
                <div className="total-tiles total">
                  <p>{t('Total Active Loan Amount')}</p>
                  <div className="amount-box d-flex justify-content-center">
                    {
                      totalActiveLoanHolding?.CAD > 0 ? (<>
                        <div className="vertical">
                          <div className="amount amount-left">
                            {t('USD')}
                            <span>{formatCurrency(totalActiveLoanHolding?.USD)}&nbsp;</span>
                            {t('USD1')}
                          </div>
                          <div className="amount amount-right">
                            {t('CAD')}
                            <span>{formatCurrency(totalActiveLoanHolding?.CAD)}&nbsp;</span>
                            {t('CAD1')}
                          </div>
                        </div>
                      </>) : (<>
                        <div className="amount">
                          {t('USD')}
                          <span>{formatCurrency(totalActiveLoanHolding?.USD)}&nbsp;</span>
                          {t('USD1')}
                        </div>
                      </>)
                    }
                  </div>
                </div>
                <div className="total-tiles total">
                  <p>{t('Total Loan Payment')}</p>
                  <div className="amount-box d-flex justify-content-center">
                    {
                      totalPaymentAmount?.CAD > 0 ? (<>
                        <div className="vertical">
                          <div className="amount amount-left">
                            {t('USD')}
                            <span>{formatCurrency(totalPaymentAmount?.USD)}&nbsp;</span>
                            {t('USD1')}
                          </div>
                          <div className="amount amount-right">
                            {t('CAD')}
                            <span>{formatCurrency(totalPaymentAmount?.CAD)}&nbsp;</span>
                            {t('CAD1')}
                          </div>
                        </div>
                      </>) : (<>
                        <div className="amount">
                          {t('USD')}
                          <span>{formatCurrency(totalPaymentAmount?.USD)}&nbsp;</span>
                          {t('USD1')}
                        </div>
                      </>)
                    }
                  </div>
                </div>
              </div>
              {
                totalAccruedInterest?.USD > 0 && (<>
                  <div className="accrued-interest-full">
                    <div className='accrued-interest-title'>
                      <p className='wlf'>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5.99844 10.2812H2.39844V18.2812H5.99844V10.2812Z" fill="#7F6E4F" />
                          <path d="M13.7992 10.2812H10.1992V18.2812Hx13.7992V10.2812Z" fill="#7F6E4F" />
                          <path d="M24 20.5703H0V23.9989H24V20.5703Z" fill="#7F6E4F" />
                          <path d="M21.6 10.2812H18V18.2812H21.6V10.2812Z" fill="#7F6E4F" />
                          <path d="M12 0L0 5.71429V8H24V5.71429L12 0Z" fill="#7F6E4F" />
                        </svg>
                        Walton Land Finance
                      </p>
                      <p className='total-accrued-interest'>{t('TOTAL ACCRUED INTEREST')}</p>
                      <div className='amount-box accrued-interest-amount'>
                        <div className="amount">
                          {t('USD')}
                          <span>{formatCurrency(totalAccruedInterest?.USD)}&nbsp;</span>
                          {t('USD1')}
                        </div>
                      </div>
                      <div className='accrued-interest-view-more'>
                        <button
                          onClick={() => setShowWLFModal(true)}
                          style={{
                            background: '#F5C518', color: '#1a1a1a',
                            border: 'none', padding: '5px 14px',
                            borderRadius: '4px', cursor: 'pointer',
                            fontSize: '13px', fontWeight: '600',
                          }}
                        >
                          View More ›
                        </button>
                      </div>
                    </div>
                  </div>
                </>)
              }
              {
                totalAccruedInterest?.USD > 0 && <WLFTrendChart showModal={showWLFModal} setShowModal={setShowWLFModal} totalAccruedUSD={totalAccruedInterest?.USD} phaseData={loanInterestData} />
              }
            </div>
            {landpage(tabLAODObj)}
          </div>
        )}
      </div>
    </div>
  );
};
export default Investments;
